import React, { useState } from 'react';
import {Card} from "antd";
import { getUploadedImagePath, Modal, notifyInfo, notifySuccess, useAppState } from '@nutastic/nutastic-react-app';
import AccountsTabSection from "./AccountsTabSection";
import classnames from 'classnames';
import { CONSTANTS } from '../../../utils/constants';
import { useNuStationData } from '../../../hooks/useNuStationData';
import { useNavigation } from '../../../hooks/useNavigation';
import makeFetchCall from '../../../utils/makeFetchCall';

const componentClass = 'accountDashboard';

const AccountDashboard = ({ onAccountsTabClick, onSubscriberCheckoutClick }) => {
    const [showMembershipModal, setShowMembershipModal] = useState(false);
    const { authState, content } = useAppState();
    const { getRecentVideos, getRecentMusic } = useNuStationData();
    const { loadPage } = useNavigation();
    const { accountInfo, isSubscribed, renewalDate, hasCanceled, subType } = authState;
    const { musicCollection, videos: videoCollection } = content;
    const { email } = accountInfo;
    const cardIconClasses = classnames('cardIcon', {
        isSubscribed: isSubscribed
    });

    const onResumeOrCancelSubscription = () => {
        const confirmQuestion = hasCanceled ? 'Are you sure you want to resume your subscription?' : 'Are you sure you want to cancel your membership?';
        if (window.confirm(confirmQuestion)) {
            makeFetchCall(hasCanceled ? '/api/payments-processor/stripe/resume-subscription' : '/api/payments-processor/stripe/cancel-subscription');
            const notificationMessage = hasCanceled
                ? 'Welcome back to the party, your subscription has been resumed. The app will reload in a few seconds now that your subscription status has updated.'
                : 'Your subscription has been cancelled, we hope you will still party with us!. The app will reload in a few seconds.';
            notifyInfo('pageReloading', 'Page Will Reload', notificationMessage);
            setTimeout(() => window.location.reload(), 5500);
        }
    };

    const renderContentList = (contentList, type) => {
        const classes = classnames(`${componentClass}__item`, {
            useWideImage: type === CONSTANTS.APPKEY_VIDEOS,
        });
        return (contentList || []).map(c => {
            const {image, slug, title} = c;
            return <div className={classes} onClick={() => loadPage(`/content/${type}/${slug}`)}>
                <img src={getUploadedImagePath(image)} alt={`${title} | NuStation`} />
                <div>
                    <p>{ title }</p>
                    <p className="d-none">{ slug }</p>
                </div>
            </div>
        });
    };

    const buildRecentMusicList = () => {
        const recentMusic = getRecentMusic();
        const recentMusicObjects = [];
        (recentMusic || []).forEach((recItem, i) => {
            const matchedMusic = (musicCollection || {}).find(m => m.id === parseInt(recItem?.id));
            if (matchedMusic && i < 12) recentMusicObjects.push(matchedMusic);
        });
        return renderContentList(recentMusicObjects, CONSTANTS.APPKEY_MUSIC);
    };

    const buildRecentVideosList = () => {
        const recentVideos = getRecentVideos();
        const recentVideoObjects = [];
        (recentVideos || []).forEach((recItem, i) => {
            const matchedVideo = (videoCollection || {}).find(v => v.id === parseInt(recItem?.id));
            if (matchedVideo && i < 16) recentVideoObjects.push(matchedVideo);
        });
        return renderContentList(recentVideoObjects, CONSTANTS.APPKEY_VIDEOS);
    };

    return <section className={componentClass}>
        <AccountsTabSection title="Account Details" text="Review / manage your account info">
            <div className="account-cards">
                <Card title="Profile Information" className={`${componentClass}__card accountInfoCard`}>
                    <div className="cardIcon"><i className="fa fa-user-circle-o" /></div>
                    <p>Your are currently logged in as:</p>
                    <h2>{ email }</h2>
                    <div className="cardButtons">
                        <button className="btn btn-small btn-outline-primary" onClick={onAccountsTabClick}>
                            Account Settings
                        </button>
                    </div>
                </Card>
                <Card title="Subscription Status" className={`${componentClass}__card accountInfoCard`}>
                    <div className={cardIconClasses}>
                        { isSubscribed ? <i className="fa fa-check-circle" /> : <i className="fa fa-times-circle" /> }
                    </div>
                    { (isSubscribed && subType === 'membership') && <p>Thank you for being a supporting explorer of the NuStation, we hope you enjoy exploring and experiencing the music. Your membership is valid through <b>{renewalDate}</b>.</p> }
                    { (isSubscribed && !hasCanceled && subType !== 'membership') && <p>Thank you for being a supporting explorer of the NuStation, we hope you enjoy exploring and experiencing the music. Your next renewal is on <b>{renewalDate}</b>.</p> }
                    { (isSubscribed && hasCanceled) && <p>Thank you for being an explorer of the NuStation, we hope you decide to return and continue your journey through sound with us. Your membership is set to expire on <b>{renewalDate}</b>.</p> }
                    { !isSubscribed && <p>You are currently not subscribed. Click the "Subscribe" button below if you would like to renew. Whether you purchase a seat or fly for free, we hope you continue to enjoy exploring the NuStation.</p> }
                    <div className="cardButtons">
                        { isSubscribed && <button className="btn btn-small btn-outline-primary" onClick={() => setShowMembershipModal(true)}>Manage Membership</button> }
                        { !isSubscribed && <button className="btn btn-small btn-outline-primary" onClick={onSubscriberCheckoutClick}>Subscribe To The NuStation</button> }
                    </div>
                </Card>
            </div>
        </AccountsTabSection>
        <AccountsTabSection title="Recent Music" text="Music you listened to recently">
            <div className={`${componentClass}__list`}>
                { buildRecentMusicList() }
            </div>
        </AccountsTabSection>
        <AccountsTabSection title="Recent Videos" text="Videos you recently viewed">
            <div className={`${componentClass}__list`}>
                { buildRecentVideosList() }
            </div>
        </AccountsTabSection>

        <Modal
            show={showMembershipModal}
            onClose={() => setShowMembershipModal(false)}
            closeOnOuterClick={false}
            title="NuStation Membership"
            className="accountsSubModal membershipModal"
            nodeId="root"
        >
            <div>
                { (isSubscribed && subType === 'membership') && <p>Your membership is valid through <b>{renewalDate}</b>. After that date, you will need to manually renew.</p> }
                { (isSubscribed && !hasCanceled && subType !== 'membership') && <p>Your next renewal is on <b>{renewalDate}</b>.</p> }
                { (isSubscribed && hasCanceled) && <p>Your membership has been canceled. Your membership is set to expire on <b>{renewalDate}</b>.</p> }
                { subType !== 'membership' && <button onClick={onResumeOrCancelSubscription} className="btn btn-primary mt-5">{!hasCanceled ? 'Cancel' : 'Resume'} Membership</button> }
            </div>
        </Modal>
    </section>;
};

export default AccountDashboard;
